import { APP } from 'src/Configs/App'
import { rtkApi } from 'src/Services/rtkApi'

import {
  AdVisualDescriptionQuestion,
  AdCampaignVisualTypeEnum,
  CreateAdCampaignResponse,
} from '../ad-campaignApi'
import { MetaCampaignsListResponse } from '../campaignList-process/api-actions'

export type UpdateMetaAdsCampaignBody = {
  id: number
} & Partial<{
  countries: string[]
  countriesTier: string
  languages: string[]
  genres: string[]
  similarArtistNames: string[]
  dailyBudget: number
  daysCount: number
  visualType: AdCampaignVisualTypeEnum
  adVisualDescription: AdVisualDescriptionQuestion[]
}>

type PerformanceTotals = {
  totalImpressions?: number
  totalClicks?: number
  lastFollowers?: number
  lastFollowersDelta?: number
  totalUniqueClicks?: number
  lastUpdatedAt?: string
  totalConversions?: number | null
}

export type CountryPerformanceItem = {
  country: string
  totalActualSpend: number
  totalClicks: number
  totalImpressions: number
  totalUniqueClicks: number
  totalConversions?: number
}

export type PerformanceDetails = {
  clicks: number
  date?: string
  followers: number
  followersAddedSinceStart: number
  impressions?: number
  uniqueClicks?: number
  conversions: number
}

type PerformanceDetailsBody = {
  dateFrom: string
  dateTo: string
  adCampaignId: string
}

export type AdVisualPerformanceItem = {
  adVisualId: number
  name: string
  thumbnailUrl: string
  totalActualSpend: number
  totalClicks: number
  totalImpressions: number
  totalUniqueClicks: number
  totalConversions?: number
}

type AdVisualVideoResponse = {
  videoUrl: string
}

type GetAdVisualVideoArgs = {
  campaignId: string
  adVisualId: string
}

const adsCampaignApi = rtkApi.injectEndpoints({
  endpoints: (builder) => ({
    getAdsCampaignsList: builder.query<MetaCampaignsListResponse, void>({
      query: () => ({
        url: `${APP.TIKTOK_SERVER}/artist/ad-campaigns`,
        method: 'GET',
      }),
    }),
    updateMetaAdsCampaign: builder.mutation<void, UpdateMetaAdsCampaignBody>({
      query: ({ id, ...body }) => ({
        url: `${APP.TIKTOK_SERVER}/artist/ad-campaigns/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['MetaAdCampaign'],
    }),

    getMetaAdsCampaignById: builder.query<CreateAdCampaignResponse, number>({
      query: (id) => ({
        url: `${APP.TIKTOK_SERVER}/artist/ad-campaigns/${id}`,
        method: 'GET',
      }),
      providesTags: ['MetaAdCampaign'],
    }),
    getPerformanceTotalsById: builder.query<PerformanceTotals, string>({
      query: (id) => ({
        url: `${APP.TIKTOK_SERVER}/artist/ad-campaigns/${id}/performance/totals`,
        method: 'GET',
      }),
    }),
    getPerformanceDetailsById: builder.query<
      PerformanceDetails[],
      PerformanceDetailsBody
    >({
      query: ({ adCampaignId, dateFrom, dateTo }) => ({
        url: `${APP.TIKTOK_SERVER}/artist/ad-campaigns/${adCampaignId}/performance/detailed?dateFrom=${dateFrom}&dateTo=${dateTo}`,
        method: 'GET',
      }),
    }),
    getAdVisualPerformanceById: builder.query<
      AdVisualPerformanceItem[],
      string
    >({
      query: (id) => ({
        url: `${APP.TIKTOK_SERVER}/artist/ad-campaigns/${id}/performance/by-ad`,
        method: 'GET',
      }),
    }),
    getCountryPerformanceById: builder.query<CountryPerformanceItem[], string>({
      query: (id) => ({
        url: `${APP.TIKTOK_SERVER}/artist/ad-campaigns/${id}/performance/by-country`,
        method: 'GET',
      }),
    }),
    getAdVisualVideoById: builder.query<
      AdVisualVideoResponse,
      GetAdVisualVideoArgs
    >({
      query: ({ campaignId, adVisualId }) => ({
        url: `${APP.TIKTOK_SERVER}/artist/ad-campaigns/${campaignId}/ad-visuals/${adVisualId}/video`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
    }),
    setAutoRenewal: builder.mutation<
      void,
      { id: number; shouldAutoRenew: boolean }
    >({
      query: ({ id, shouldAutoRenew }) => ({
        url: `${APP.TIKTOK_SERVER}/artist/ad-campaigns/${id}/auto-renewal`,
        method: 'PATCH',
        body: { shouldAutoRenew },
      }),
      invalidatesTags: ['MetaAdCampaign'],
    }),
  }),
})

export const {
  useGetAdsCampaignsListQuery,
  useUpdateMetaAdsCampaignMutation,
  useGetMetaAdsCampaignByIdQuery,
  useGetPerformanceTotalsByIdQuery,
  useGetPerformanceDetailsByIdQuery,
  useGetAdVisualPerformanceByIdQuery,
  useGetCountryPerformanceByIdQuery,
  useGetAdVisualVideoByIdQuery,
  useSetAutoRenewalMutation,
} = adsCampaignApi
